import { useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Tabs reparte una pantalla larga en secciones.
 *
 * # Cuándo usarlo y cuándo no
 *
 * Cuando una pantalla trata **un solo asunto** pero con tareas distintas que
 * casi nunca se hacen a la vez: los límites de una cuenta son los límites de
 * salida, la vigilancia antifraude y la lista de no-llamar. Verlo todo seguido
 * no ayuda a nadie; es un formulario de treinta campos donde cuesta encontrar
 * el que se venía a cambiar.
 *
 * **No** para esconder algo que hay que ver sí o sí. Una alarma —el kill
 * switch encendido, un error de carga— va fuera de las pestañas: detrás de una
 * pestaña cerrada no existe.
 *
 * # Por qué va en la URL
 *
 * Para poder mandar «mira la lista de no-llamar» con un enlace, y para que
 * recargar no devuelva a la primera sección. Se reemplaza la entrada del
 * historial en lugar de apilarla: con `push`, volver atrás recorrería una por
 * una todas las pestañas visitadas en vez de salir de la pantalla.
 *
 * # Teclado
 *
 * Flechas para moverse entre pestañas, Inicio y Fin para los extremos, que es
 * lo que un lector de pantalla anuncia al entrar en un `tablist`. Sin esto,
 * tabular mete el foco en cada pestaña una por una antes de llegar al
 * contenido.
 */
export function Tabs({
  param,
  tabs,
}: {
  /** Nombre del parámetro de la URL. Distinto por pantalla. */
  param: string;
  tabs: { id: string; label: string; badge?: number; content: () => React.ReactNode }[];
}) {
  const [params, setParams] = useSearchParams();
  const botones = useRef<(HTMLButtonElement | null)[]>([]);

  const pedida = params.get(param);
  // Una pestaña que no existe —un enlace viejo, una errata— cae en la primera
  // en lugar de dejar la pantalla en blanco.
  const indice = Math.max(
    0,
    tabs.findIndex((t) => t.id === pedida),
  );
  const activa = tabs[indice];

  function elegir(i: number) {
    const tab = tabs[i];
    if (tab === undefined) return;
    const siguientes = new URLSearchParams(params);
    siguientes.set(param, tab.id);
    setParams(siguientes, { replace: true });
    botones.current[i]?.focus();
  }

  function enTeclado(e: React.KeyboardEvent) {
    const movimientos: Record<string, number> = {
      ArrowRight: indice + 1,
      ArrowLeft: indice - 1,
      Home: 0,
      End: tabs.length - 1,
    };
    const destino = movimientos[e.key];
    if (destino === undefined) return;
    e.preventDefault();
    elegir((destino + tabs.length) % tabs.length);
  }

  return (
    <>
      <div
        role="tablist"
        onKeyDown={enTeclado}
        className="mb-5 flex flex-wrap gap-1 border-b border-white/10"
      >
        {tabs.map((tab, i) => {
          const seleccionada = i === indice;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                botones.current[i] = el;
              }}
              role="tab"
              id={`tab-${param}-${tab.id}`}
              aria-selected={seleccionada}
              aria-controls={`panel-${param}-${tab.id}`}
              // Solo la pestaña activa entra en el orden de tabulación: las
              // demás se alcanzan con las flechas, que es como funciona un
              // tablist.
              tabIndex={seleccionada ? 0 : -1}
              onClick={() => elegir(i)}
              className={
                "-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition " +
                (seleccionada
                  ? "border-sky-400 text-white"
                  : "border-transparent text-slate-400 hover:border-white/20 hover:text-slate-200")
              }
            >
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="rounded-md bg-white/10 px-1.5 py-0.5 text-[11px] tabular-nums text-slate-300">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activa !== undefined && (
        <div
          role="tabpanel"
          id={`panel-${param}-${activa.id}`}
          aria-labelledby={`tab-${param}-${activa.id}`}
          tabIndex={0}
        >
          {/* El contenido es una función: así la pestaña que no se ve no se
              monta, y una sección cara no cuesta nada mientras nadie la abra. */}
          {activa.content()}
        </div>
      )}
    </>
  );
}
